<template>
  <div>
    <v-row dense>
      <v-col class="py-2">
        <qtm-input-label label="Company Name" required>
          <slot
            name="name"
            :error-messages="errors.vendor.name"
            hide-details="auto"
            :maxlength="vendorNameMaxLength"
            :model-value="v$.vendor.name.$model"
            @update:model-value="v$.vendor.name.$model = $event"
          >
            <qtm-text-field
              v-model="v$.vendor.name.$model"
              :error-messages="errors.vendor.name"
              hide-details="auto"
              :maxlength="vendorNameMaxLength"
            />
          </slot>
        </qtm-input-label>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col class="py-2">
        <qtm-input-label label="Street Address" :required="detailsRequired">
          <qtm-text-field
            v-model="v$.vendor.address.$model"
            :error-messages="errors.vendor.address"
            hide-details="auto"
            :maxlength="addressMaxLength"
          />
        </qtm-input-label>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col class="py-2" cols="6">
        <qtm-input-label label="Country" :required="detailsRequired">
          <country-select
            v-model="vendor.country"
            hide-details="auto"
            :error-messages="errors.vendor.country"
            @update:model-value="countryChanged"
          />
        </qtm-input-label>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col class="py-2" cols="6">
        <qtm-input-label :label="provinceLabel" :required="detailsRequired">
          <province-select
            v-model="v$.vendor.province.$model"
            :country="vendor.country"
            hide-details="auto"
            :error-messages="errors.vendor.province"
            :maxlength="addressMaxLength"
          />
        </qtm-input-label>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col class="py-2" cols="6">
        <qtm-input-label label="City" :required="detailsRequired">
          <qtm-text-field
            v-model="v$.vendor.city.$model"
            :error-messages="errors.vendor.city"
            hide-details="auto"
            :maxlength="addressMaxLength"
            @update:model-value="cityChanged"
          />
        </qtm-input-label>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col class="py-2" cols="6">
        <qtm-input-label :label="postalCodeLabel" :required="detailsRequired">
          <postal-code-input
            v-model="v$.vendor.postal_code.$model"
            :country="vendor.country"
            :error-messages="errors.vendor.postal_code"
            hide-details="auto"
            @update:model-value="postalCodeChanged"
          />
        </qtm-input-label>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { required, requiredIf } from '@vuelidate/validators'
import { ADDRESS_MAX_LENGTH, VENDOR_NAME_MAX_LENGTH } from '@/constants'
import CountrySelect from '@/components/country-select.vue'
import PostalCodeInput from '@/components/postal-code-input.vue'
import ProvinceSelect from '@/components/province-select.vue'
import useValidation from '@/composables/validation'

export default {
  name: 'vendor-form',
  components: { CountrySelect, PostalCodeInput, ProvinceSelect },
  props: {
    countryCode: {
      type: String,
      required: false,
      default: 'CA',
    },
    detailsRequired: {
      type: Boolean,
      default: false
    },
    vendor: {
      type: Object,
      required: true
    },
  },
  setup() {
    const { errors, isValid, v$ } = useValidation()

    return { errors, isValid, v$ }
  },
  validations: {
    vendor: {
      name: { required },
      address: {
        required: requiredIf(function () {
          return this.detailsRequired
        })
      },
      city: {
        required: requiredIf(function () {
          return this.detailsRequired
        })
      },
      country: {
        required: requiredIf(function () {
          return this.detailsRequired
        })
      },
      province: {
        required: requiredIf(function () {
          return this.detailsRequired
        })
      },
      postal_code: {
        required: requiredIf(function () {
          return this.detailsRequired
        }),
      }
    },
  },
  data() {
    return {
      addressMaxLength: ADDRESS_MAX_LENGTH,
      vendorNameMaxLength: VENDOR_NAME_MAX_LENGTH,
    }
  },
  computed: {
    isCanada() {
      return this.vendor.country !== 'US'
    },
    postalCodeLabel() {
      return this.isCanada ? 'Postal Code' : 'Zipcode'
    },
    provinceLabel() {
      return this.isCanada ? 'Province' : 'State'
    }
  },
  created() {
    if (!this.vendor.country) {
      this.vendor.country = this.countryCode
    }
  },
  methods: {
    cityChanged() {
      this.vendor.city = this.vendor.city.charAt(0).toUpperCase() + this.vendor.city.slice(1)
    },
    async countryChanged() {
      await this.$nextTick()
      this.vendor.postal_code = ''
      this.v$.vendor.postal_code.$reset()
      this.vendor.province = ''
      this.v$.vendor.province.$reset()
    },
    postalCodeChanged() {
      if (!this.vendor.postal_code.length) {
        this.v$.vendor.postal_code.$reset()
      }
    },
    resetValidation() {
      this.v$.$reset()
    },
  }
}
</script>
